/* eslint-disable */
import _get from 'lodash/get';
import _unset from 'lodash/unset';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _remove from 'lodash/remove';

import router from '@/router';
import { post, isPresent } from '@/lib/utils';
import { constants } from '@/lib/constants';
import { insertKeys, defaultKeys } from '@/lib/vuexUtils';

/**
 * Uses a given router path and classname to get cached data from state
 * Needed to sidestep lodash _get's inability to handle periods in keys
 * @param {Object} stateObj - base state object
 * @param {String} [basePath = ''] - base path to get main cache object from stateObj
 * @param {String} [path = ''] - router path of cached data
 * @param {String} [className = ''] - classname of cached data
 * @returns {Any} - cached data
 */
const getFromClassPath = ({ stateObj, basePath = '', path = '', className = '', defaultValue = null }) => {
  const obj = _get(stateObj, basePath, {});
  return obj[`${path}_${className}`] || defaultValue;
};

const updateCachedResultCount = ({ state, className, path, count, cachedSearch }) => {
  // We don't get pagination info back in this case, so we have to manually update the recordCount
  const cachedRecordCount = _get(cachedSearch, 'pagination.pagination.recordCount');
  if (isPresent(cachedRecordCount)) {
    _set(state, `cached.userCache.cachedSearch[${path}_${className}].paginationrecordCount`, count);
  }
};

const localState = {
  // camelCase
  activeRequests: {},
  autoupdate_dependencies: {},
  cached: {
    lazyloadQueries: {},
    dataProviders: {},
    userCache: {
      cachedSearch: {},
      searchStrings: {},
    },
  },
  client_config: {},
  custommetadata: {},
  database_name: '',
  dataproviders: {},
  field_authentications: {},
  public_key: {},
  retailer_token: '',
  sites: [],
  systemparameters: [],
  previousScreenData: {},
  allowedClassNames: [
    'CustomerOrder',
    'Order', // NOTE: This is to get Order Search to render Continue Editing button
    'Customer',
    'Product',
    'Invoice',
  ],
};

const localGetters = {
  // underscore_lowercase
  get_provider: state => name => {
    return name ? state.dataproviders[name.toLowerCase()] : null;
  },

  get_provider_data: state => name => {
    return state.cached.dataProviders[name];
  },

  get_all_providers_data: state => {
    return state.cached.dataProviders;
  },

  get_lazyload_query: state => key => {
    return state.cached.lazyloadQueries[key];
  },

  get_site_label: state => siteid => {
    const result = state.sites.find(site => siteid === site.siteid);
    return _get(result, '_ui_label', siteid);
  },

  get_user_cache: state => state.cached.userCache,

  get_search_cache: state => ({ className, path }) => {
    return getFromClassPath({
      stateObj: state,
      basePath: 'cached.userCache.cachedSearch',
      path,
      className,
      defaultValue: {},
    });
  },

  searchString: state => ({ className, path }) => {
    return getFromClassPath({
      stateObj: state,
      basePath: 'cached.userCache.searchStrings',
      path,
      className,
      defaultValue: '',
    });
  },

  additionalSearchCache: state => ({ className, path }) => {
    const cachedSearch = getFromClassPath({
      stateObj: state,
      basePath: 'cached.userCache.cachedSearch',
      path,
      className,
      defaultValue: {},
    });
    return cachedSearch.additionalData;
  },

  canCacheClass: state => className => state.allowedClassNames.includes(className),

  hasCachedRecord: state => className => Object.keys(state.previousScreenData).includes(className),

  previousScreenData: state => state.previousScreenData,

  activeRequests: state => state.activeRequests,

};

const localActions = {
  // camelCase
  initializeCached({ commit }, data) {
    commit('initialize_cached_state', data);
  },

  initializeVisibleSites({ commit }, data) {
    commit('set_data', { data, clsName: 'Site', key: 'visiblesites' });
  },

  /**
  * Makes request to server to get array of items related to dataprovider
  * If the key already exists in state.cached it will pull from there
  * If a key is passed in the results are set to that key in state.cached
  * If no key is passed in the results are returned without setting the results in state.cached
  * @param {Object} dataProviderObj - Object containing url and httpParams
  * @param {String} [key = ''] - String used to set dataProvider results to in state.cached
  * @returns {Object[]} Array of objects or strings from dataProvider call
  */
  async getDataProviderData({ commit, state, rootState, dispatch }, { key, dataProviderObj = null }) {
    if (key.includes(':')) [key] = key.split(':').slice(-1);
    if (state.cached.dataProviders[key]) return _get(state, `cached.dataProviders.${key}`);
    dataProviderObj = dataProviderObj || state.dataproviders[key];
    if (_isEmpty(dataProviderObj)) return;
    let results = [];
    let hasErrors = false;
    if (dataProviderObj.derived) results = await dispatch('handleDerivedProvider', { dataProviderObj, results });
    else if (!state.activeRequests[key] && dataProviderObj.url) {
      try {
        commit('set_active_request', { key, value: dataProviderObj.url });
        const params = { ...dataProviderObj.httpParams };
        if (dataProviderObj.url.match(/\/query$/)) params.serialize = '_ui_label';
        const { results: postResults, errors } = await dispatch('editObject/query', {
          url: `/${dataProviderObj.url}`,
          params,
        }, { root: true });
        if (postResults) results = postResults;
        if (errors) hasErrors = true;

        for (let item of results) {
          item = insertKeys(defaultKeys, item, rootState.meta);
        }
      } catch (e) {
        // If a request fails, make sure it doesn't get stuck in active requests
      } finally {
        commit('clear_active_request', key);
      }
    }

    /**
     * In the case of derivedFrom being an array, we need to see if ANY
     * active requests for the listed providers exist. Otherwise, it's just
     * a plain string.
     */
    const derivedProvider = _get(dataProviderObj, 'derived.derivedFrom');
    let derivedKey = derivedProvider;
    if (Array.isArray(derivedProvider)) {
      for (const provider of derivedProvider) {
        if (state.activeRequests[provider]) {
          derivedKey = provider;
          break;
        }
      }
    }

    if (state.activeRequests[key] || state.activeRequests[derivedKey]) {
      return {
        awaitData: true,
        data: [],
        clsName: dataProviderObj.clsName,
        key: derivedProvider || key,
      };
    }

    if (!key) return { data: results, clsName: dataProviderObj.clsName };
    if (!hasErrors) await commit('set_data', { data: results, clsName: dataProviderObj.clsName, key });
    return { data: results, clsName: dataProviderObj.clsName, key };
  },
  /**
   * Handles getting derived data providers. Will recurse by calling getDataProviderData
   * @param {Object} dataProviderObj the data provider object itself
   * @param {Object[]} results the growing list of results to return to caller of getDataProviderData
   */
  async handleDerivedProvider({ dispatch, rootGetters }, { dataProviderObj, results }) {
    const filterByPermission = (items) => {
      return items.reduce((acc, val) => {
        if (dataProviderObj.derived && dataProviderObj.derived.permission && val[dataProviderObj.derived.permission]) {
          const hasPermission = rootGetters['permissions/isPermPresent'](val[dataProviderObj.derived.permission]);
          if (hasPermission === false) return acc;
        }
        acc.push(val);
        return acc;
      }, []);
    };
    if (Array.isArray(dataProviderObj.derived.derivedFrom)) {
      for (const provider of dataProviderObj.derived.derivedFrom) {
        const res = await dispatch('getDataProviderData', { key: provider });
        results = [...results, ...res.data];
        if (dataProviderObj.derived.permission) results = filterByPermission(results);
      }
    } else {
      const res = await dispatch('getDataProviderData', { key: dataProviderObj.derived.derivedFrom });
      results = [...results, ...res.data];
      if (dataProviderObj.derived.permission) results = filterByPermission(results);
    }

    if (dataProviderObj.derived.filter) {
      const filter = await dispatch('buildFilterFn', dataProviderObj.derived.filter);
      results = [...results.filter((res) => filter(res))];
    }
    return results;
  },
  /**
   * Stores lazyload data/queries in state
   * @param {Object} state - Cached store state object
   * @param {Object[]} payload.key - contains lazyload key, data, and query
   */
  cacheLazyloadQuery({ commit }, payload) {
    commit('cache_lazyload_query', payload);
  },
  /**
   * Sets a key on activeRequests
   * @param {String} key - key to set on activeRequests
   * @param {String} value - value to set on activeRequests
   * @param {String} objId - _id of a particular object (for lazyloads)
   */
  setActiveRequest({ commit }, payload) {
    commit('set_active_request', payload);
  },
  /**
   * Clears a key on activeRequests
   * @param {String} key - key to clear on activeRequests
   */
  clearActiveRequest({ commit }, key) {
    commit('clear_active_request', key);
  },
  /**
   * Handles filtering of results from derived data provider using the filter property
   * @param {string} filterStr filters split by ; then by : key:value
   * @returns {Function} returns true or false depending on criteria built in initialization of function
   */
  // eslint-disable-next-line max-lines-per-function
  buildFilterFn(ctx, filterStr) {
    if (!filterStr) return null;
    const criteria = [];
    filterStr.split(';').forEach((crit) => {
      let list = crit.split(':');
      let negated = false;
      // expect only one colon (for now)
      /* istanbul ignore else */
      if (list.length === 2) {
        if (list[0].endsWith('!')) {
          negated = true;
          list[0] = list[0].substr(0, list[0].length - 1);
        }
        const attrPath = list[0].split('.');
        list = list[1].split(',');
        criteria.push({ attr: attrPath, values: list, negate: negated });
      }
    });
    if (!criteria.length) {
      // Invalid syntax: filter them all out
      return () => false;
    }
    const getChainedAttr = (obj, chain) => {
      obj = obj[chain[0]];
      if (chain.length === 1 || obj == null) {
        return obj;
      }
      return getChainedAttr(obj, chain.slice(1));
    };
    return (o) => {
      let met = true;
      for (const c of criteria) {
        const value = getChainedAttr(o, c.attr);
        if (c.values.length === 1 && c.values[0] === '*') {
          // wildcard '*'
          if ((!c.negate && value) || (c.negate && !value)) {
            // good: value isn't null (or is null with negate)
            continue;
          }
          met = false;
          break;
        }
        if (c.values.findIndex(val => val === value) === -1) {
          // did not match any value
          if (!c.negate) {
            met = false;
            break;
          }
        } else {
          // matched value
          // eslint-disable-next-line no-lonely-if
          if (c.negate) {
            met = false;
            break;
          }
        }
      }
      return met;
    };
  },

  async getLocations(ctx, siteid) {
    // TODO: caching of none data provider data
    const { results } = await post('/tg/site/get_locations', { siteid });
    return results;
  },

  async getLockStatuses({ dispatch }, payload) {
    const { locations, locationid } = payload;
    if (!locationid) return [];
    const location = locations.find(item => item.locationid === locationid);

    if (!location) return [];

    const key = location.type === 'O' ? 'outletstatuses' : 'lockstatuses';

    const { data } = await dispatch('getDataProviderData', { key });
    return data;
  },

  async enabledCashDrawersBySite(ctx, siteid) {
    const { EQUALS, DB_TRUE, DELIM } = constants;
    const url = '/tg/cashdrawer/query';
    const q = `siteid-${EQUALS}${siteid}${DELIM}enabled-${EQUALS}${DB_TRUE}${DELIM}`;
    const params = { q, orderby: 'cashdrawerid', limit: 'None' };
    const { results } = await post(url, params);
    return results;
  },

  clearDataProviders({ commit }) {
    commit('clear_data_providers');
  },

  removeDataProvider({ commit }, className) {
    commit('remove_provider', className);
  },

  updateSearchString({ commit }, payload) {
    commit('set_search_string', payload);
  },

  updateCachedSearch({ commit }, payload) {
    commit('update_cached_search', payload);
  },

  refreshCachedSearch({ commit }, payload) {
    commit('refresh_cached_search', payload);
  },

  clearUserCache({ commit, dispatch }) {
    commit('clear_user_cache');
    dispatch('advancedSearch/clearLocalState', {}, { root: true });
  },

  clearStaleDataproviders({ commit }, className) {
    commit('clear_stale_dataproviders', className);
  },

  removeRecordsFromCachedSearch({ commit }, payload) {
    commit('remove_from_cached_search', payload);
  },

  updateCachedSearchRecords({ commit, rootState, rootGetters, dispatch }, payload) {
    commit('update_cached_search_results', { ...payload, rootState, rootGetters, dispatch });
  },

  updateAdditionalSearchCache({ commit }, payload) {
    commit('update_additional_search_cache', payload);
  },

  cacheScreenData({ commit, getters }, { model = {}, routeName, id, className }) {
    if (!className || !getters.canCacheClass(className)) return;
    commit('cache_screen_data', { model, className, routeName, id });
  },

  updateCachedScreenModel({ commit, getters }, { model = {}, className }) {
    if (!className || !getters.canCacheClass(className)) return;
    commit('update_cached_model', { model, className });
  },

  updateCachedDirtyFlag({ commit, getters }, { isDirty, className }) {
    if (!className || !getters.canCacheClass(className)) return;
    commit('update_cached_dirty_flag', { isDirty, className });
  },

  invalidateScreenDataByClassName({ commit }, className) {
    commit('invalidate_screen_data_by_class_name', className);
  },

  invalidateScreenData({ commit }) {
    commit('invalidate_screen_data');
  },
};

const localMutations = {
  // underscore_case
  initialize_cached_state(state, value) {
    Object.assign(state, value);
  },

  /**
   *
   * Sets a key on activeRequests
   * @param {String} key - key to set on activeRequests
   * @param {String} value - value to set on activeRequests
   */
  set_active_request(state, { key, value, objId }) {
    if (objId) state.activeRequests[key] = { value, objId };
    else state.activeRequests[key] = value;
  },

  /**
   * Clears a key on activeRequests
   * @param {String} key - key to clear on activeRequests
   */
  clear_active_request(state, key) {
    _unset(state, `activeRequests.${key}`);
  },

  /**
   * Stores results from getDataProviderData
   * @param {Object} state - Cached store state object
   * @param {Object[]} value.data - Array of results
   * @param {Object} value.dataProviderObj - Object containing props for querying
   * @param {String} value.key - String used to set data to in store
   */
  set_data(state, value) {
    const { data, clsName, key } = value;
    _set(state, `cached.dataProviders.${key}`, { data, clsName });
  },

  /**
   * Stores lazyload data/queries in state
   * @param {Object} state - Cached store state object
   * @param {Object[]} payload.key - lazyload key
   * @param {Object[]} payload.data - lazyloaded data
   * @param {Object} payload.query - UQL of last query
   */
  cache_lazyload_query(state, payload) {
    const { key, query } = payload;
    _set(state, `cached.lazyloadQueries.${key}`, query);
  },

  /**
   * Sets cached dataproviders prop to an empty object
   * @param {Object} state - cached store state object
   */
  clear_data_providers(state) {
    state.cached = {
      ...state.cached,
      dataProviders: {},
    };
  },

  /**
   * Removes single cached data provider results by class name
   * @param {Object} state - cached store state object
   * @param {String} className - className used to find cached.dataProviders prop to delete
   */
  remove_provider(state, className) {
    const providers = state.cached.dataProviders;
    for (const [key, value] of Object.entries(providers)) {
      if (value.className === className) _unset(state, `cached.dataProviders.${key}`);
    }
  },

  update_cached_search(state, { className, keys, path, clearSearch = false, isPaginated = false }) {
    if (!path) return;

    const getCachedSearchObj = () => {
      return getFromClassPath({
        stateObj: state,
        basePath: 'cached.userCache.cachedSearch',
        path,
        className,
        defaultValue: null,
      });
    };

    const cachedSearch = state.cached.userCache.cachedSearch;
    let currentCachedSearch = getCachedSearchObj();

    if (clearSearch) {
      _unset(cachedSearch, `${path}_${className}`);
      return;
    }
    if (!currentCachedSearch) {
      _set(cachedSearch, `${path}_${className}`, {}); // Create cached path if there isn't one
      currentCachedSearch = getCachedSearchObj();
    }

    for (const key of Object.keys(keys)) {
      if (key === 'results') {
        const results = currentCachedSearch[key];
        if (results && results.length) {
          if (isPaginated) {
            _set(currentCachedSearch, key, keys[key]);
            continue;
          } else {
            _set(currentCachedSearch, key, results.concat(keys[key]));
            continue;
          }
        }
      }
      _set(currentCachedSearch, key, keys[key]);
    }
  },

  refresh_cached_search(state, { className, path, isAdvSearch = false }) {
    if (!path) return;

    const currentCachedSearch = getFromClassPath({
      stateObj: state,
      basePath: 'cached.userCache.cachedSearch',
      path,
      className,
      defaultValue: null,
    });

    if (!currentCachedSearch) return;
    _set(currentCachedSearch, 'results', []);
    _set(currentCachedSearch, 'recordCount', null);
    _set(currentCachedSearch, 'offset', 0);
    if (isAdvSearch) {
      _set(currentCachedSearch, 'args', null);
      _set(currentCachedSearch, 'filterid', null);
    }
  },

  clear_user_cache(state) {
    _set(state, 'cached.userCache', { cachedSearch: {}, searchStrings: {} });
  },

  clear_stale_dataproviders(state, className) {
    const dataProviders = _get(state, 'cached.dataProviders', {});
    const keys = Object.keys(dataProviders);

    for (const key of keys) {
      const provider = dataProviders[key];
      const classNameMatches = provider.clsName instanceof Array ?
        provider.clsName.includes(className) :
        provider.clsName === className;
      if (classNameMatches) _unset(dataProviders, key);
    }
  },

  set_search_string(state, { className, searchString }) {
    _set(state, `cached.userCache.searchStrings.${router.currentRoute._rawValue.path}_${className}`, searchString);
  },

  remove_from_cached_search(state, { className, records, path }) {
    const cachedSearch = _get(state, 'cached.userCache.cachedSearch');
    if (!cachedSearch) return;

    // We're looking through EVERY cached search path for a given classname
    // as the deleted record(s) could be in multiple places.
    for (const [key, val = {}] of Object.entries(cachedSearch)) {
      if (!key.endsWith(className) || !val.results || !val.results.length) continue;

      const results = [...val.results];
      _remove(results, (res) => {
        for (const { _id } of records) {
          return _id === res._id;
        }
      });

      if (results.length === val.results.length) continue; // Nothing was removed
      _set(cachedSearch, `${path}_${className}.results`, results);
      const currentCachedSearch = getFromClassPath({
        stateObj: state,
        basePath: 'cached.userCache.cachedSearch',
        path,
        className,
        defaultValue: null,
      });
      updateCachedResultCount({
        state,
        className,
        path,
        count: results.length,
        cachedSearch: currentCachedSearch,
      });
    }
  },

  update_cached_search_results(state, payload) {
    // eslint-disable-next-line max-len
    const { rootState, rootGetters, dispatch, className, records = [], refreshed = [], path, shouldInsertKeys = false, keepMessages = false, clearResults = false } = payload;

    const currentCachedSearch = getFromClassPath({
      stateObj: state,
      basePath: 'cached.userCache.cachedSearch',
      path,
      className,
      defaultValue: {},
    });

    if (!currentCachedSearch.results || !currentCachedSearch.results.length) return; // no need to update cache if there is nothing to update
    if (clearResults) {
      _set(currentCachedSearch, 'results', []);
      _set(currentCachedSearch, 'recordCount', {});
      return;
    }

    // insertkeys into refresh_others items
    const keysToInsert = [{ key: '_id', defaultValue: null, isPrimaryKey: true }];
    refreshed.forEach(ref => insertKeys(keysToInsert, ref, rootState.meta));
    let results = [...currentCachedSearch.results];

    const deletedRecordMap = {};

    for (const rec of records) {
      for (const [index, res] of results.entries()) {
        if (shouldInsertKeys) insertKeys(keysToInsert, rec, rootState.meta);

        // handling for refreshed_others
        const recToBeRefreshed = refreshed.find(ref => ref._id === res._id);
        if (recToBeRefreshed) _set(results, `[${index}]`, { ...res, ...recToBeRefreshed });

        // Remove errors/warnings from cached results
        if (keepMessages && res._id === rec._id) {
          if (res._errors) _unset(results, `[${index}]._errors`);
          if (res._warnings) _unset(results, `[${index}]._warnings`);
        }

        if (res._id === rec._id && (rec._errors || rec._warnings)) {
          for (const key of Object.keys(res)) {
            results[index][key] = rec[key];

            // Copy errors/warnings, if told, from updated record to cached results
            if (keepMessages && rec._errors) _set(results, `[${index}]._errors`, rec._errors);
            if (keepMessages && rec._warnings) _set(results, `[${index}]._warnings`, rec._warnings);
          }
          break;
        }

        // Check if record is being deleted
        if (rec._deleted) {
          deletedRecordMap[rec._id] = true;
          continue;
        }

        // Check if record needs to be updated
        if (rec._id === res._id) {
          // Avoid object reference drama by applying values of returned record instead of replacing the entire object
          for (const k of Object.keys(rec)) res[k] = rec[k];
        }
      }
    }

    // Remove any records from the search cache that have been marked as _deleted
    const deleteCount = Object.keys(deletedRecordMap).length;
    if (deleteCount) {
      results = results.filter(item => {
        return !deletedRecordMap[item._id];
      });

      dispatch('notify/push', {
        title: 'Records Deleted',
        text: rootGetters['translations/rawTranslate']('StaleRecordsDeleted'),
        type: 'info',
      }, { root: true });

      _set(state, `cached.userCache.cachedSearch[${path}_${className}].results`, results);
      // We don't get pagination info back in this case, so we have to manually update the recordCount
      updateCachedResultCount({
        state,
        className,
        path,
        count: results.length,
        cachedSearch: currentCachedSearch,
      });
    }
  },

  update_additional_search_cache(state, { className, path, keys }) {
    _set(state, `cached.userCache.cachedSearch[${path}_${className}].additionalData`, keys);
  },

  cache_screen_data(state, { model, className, routeName, id }) {
    state.previousScreenData[className] = { model, routeName, id }; // NOTE: Leveraging of object references to keep editObject/record in sync
  },

  update_cached_model(state, { className, model }) {
    state.previousScreenData[className].model = model;
  },

  update_cached_dirty_flag(state, { className, isDirty }) {
    state.previousScreenData[className].isDirty = isDirty;
  },

  invalidate_screen_data_by_class_name(state, className) {
    _unset(state, `previousScreenData.${className}`);
  },

  invalidate_screen_data(state) {
    state.previousScreenData = {};
  },
};

export default {
  namespaced: true,
  state: localState,
  getters: localGetters,
  actions: localActions,
  mutations: localMutations,
};
